import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useDefaultTradingMode from '../hooks/useDefaultTradingMode';
import {
  CHAIN_LABELS, CHAIN_COLORS, STRATEGY_LABELS,
  formatNum, formatTime, activityDotColor, formatActivityLabel,
} from '../utils/masterChefUtils';

export default function MasterChefActivity() {
  const [activity, setActivity] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useDefaultTradingMode('');
  const [type, setType] = useState('');
  const [chain, setChain] = useState('');
  const [strategy, setStrategy] = useState('');
  const [page, setPage] = useState(0);
  const limit = 100;

  const fetchActivity = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit, skip: page * limit });
      if (mode) params.set('mode', mode);
      if (type) params.set('type', type);
      if (chain) params.set('chain', chain);
      if (strategy) params.set('strategy', strategy);
      const res = await api.get(`/masterchef/activity?${params}`);
      setActivity(res.data.activity || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoading(false);
    }
  }, [mode, type, chain, strategy, page]);

  useEffect(() => {
    fetchActivity();
    const interval = setInterval(fetchActivity, 15000);
    return () => clearInterval(interval);
  }, [fetchActivity]);

  useEffect(() => { setPage(0); }, [mode, type, chain, strategy]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Activity Feed</h2>
          <p className="text-xs text-gray-500">{total} events total — entries, tier exits, stop losses, trailing stops</p>
        </div>
        <Link to="/trading/masterchef/activity" className="text-xs text-blue-500 hover:underline self-start">
          View activity feed →
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        <select value={mode} onChange={(e) => setMode(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All modes</option>
          <option value="live">Live</option>
          <option value="dry_run">Dry Run</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All types</option>
          <option value="entry">Entries</option>
          <option value="exit_tier">Tier Exits</option>
          <option value="stop_loss">Stop Loss</option>
          <option value="trailing_stop">Trailing Stop</option>
        </select>
        <select value={chain} onChange={(e) => setChain(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All chains</option>
          <option value="solana">Solana</option>
          <option value="base">Base</option>
          <option value="ethereum">Ethereum</option>
          <option value="bsc">BSC</option>
        </select>
        <select value={strategy} onChange={(e) => setStrategy(e.target.value)}
          className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
          <option value="">All strategies</option>
          <option value="recovery">Recovery</option>
          <option value="pump">Pump</option>
          <option value="volume">Volume</option>
          <option value="ema">EMA</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading && activity.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading activity...</div>
        ) : activity.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">No activity matches filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="text-left px-4 py-2">Time</th>
                  <th className="text-left px-2 py-2">Event</th>
                  <th className="text-left px-2 py-2">Token</th>
                  <th className="text-left px-2 py-2">Chain</th>
                  <th className="text-left px-2 py-2">Strategy</th>
                  <th className="text-center px-2 py-2">Mode</th>
                  <th className="text-right px-2 py-2">Amount</th>
                  <th className="text-right px-2 py-2">Price</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((a) => (
                  <tr key={a.id} className="border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {formatTime(a.timestamp)}
                      <div className="text-[10px] text-gray-400">
                        {a.timestamp ? new Date(a.timestamp).toLocaleString() : '-'}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${activityDotColor(a.type)}`} />
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{formatActivityLabel(a)}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 font-medium text-gray-900 dark:text-white">{a.symbol}</td>
                    <td className="px-2 py-2">
                      <span className={`px-1.5 py-0.5 text-[10px] rounded bg-${CHAIN_COLORS[a.chain] || 'gray'}-100 dark:bg-${CHAIN_COLORS[a.chain] || 'gray'}-900/30`}>
                        {CHAIN_LABELS[a.chain] || a.chain}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-xs capitalize text-gray-600 dark:text-gray-400">
                      {STRATEGY_LABELS[a.strategy] || a.strategy}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${
                        a.isDryRun ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      }`}>
                        {a.isDryRun ? 'DRY' : 'LIVE'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-xs">{formatNum(a.amount, 4)}</td>
                    <td className="px-2 py-2 text-right font-mono text-xs text-gray-500">
                      {a.price ? `$${formatNum(a.price, 8)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">
              Previous
            </button>
            <span className="text-xs text-gray-500">Page {page + 1} of {totalPages} ({total} events)</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
